import { createContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import type { QueryKey } from '@tanstack/react-query';
import type { ResultOf, VariablesOf } from '@graphql-typed-document-node/core';
import type { ReactNode } from 'react';

import { getFragmentData, graphql } from '@e-socna/data-layer';
import { EMPTY_ARRAY, NULL_FUNCTION } from '@e-socna/empty';

import { userFriendlyErrors } from '../../utils/back-errors.util';
import { memoForm2ApiMapper } from '../../forms/MemoBeforeSubmit/mappers/memo.form2api.mapper';
import { useGraphQLMutation } from '../../hooks/useGraphQLMutation.hook';
import { useViewSnackbarAction } from '../../hooks/useViewSnackbarAction.hook';
import type { MemosState } from '../../forms/MemoBeforeSubmit';
import type { BackError } from '../../utils/back-errors.util';

import { useUiContext } from '../UiContext/hooks/useUiContext.hook';
import { BackgroundOperationType } from '../UiContext/enums/background-operation-type.enum';
import { MessageList } from '../../components/MessageList';

export const PrinterContextProvider_PdfPrint_Fragment = graphql(`
  fragment PrinterContextProvider_PdfPrint_Fragment on PdfPrint {
    _id
    increment
    file {
      _id
      uri
    }
    isTmp
    createdAt
    creatorId
    automaticMemo
    internalMemo
    memo
  }
`);

export type PrinterContextProvider_PdfPrint = ResultOf<
  typeof PrinterContextProvider_PdfPrint_Fragment
>;

export const PrinterContextProvider_PrintDocumentTemplate_Mutation = graphql(`
  mutation PrinterContextProvider_PrintDocumentTemplate(
    $clearTmp: Boolean!
    $isTmp: Boolean!
    $printableId: Id!
    $useCacheFirst: Boolean!
    $versionData: VersionDataInput
  ) {
    print: printDocumentTemplate(
      clearTmp: $clearTmp
      isTmp: $isTmp
      printableId: $printableId
      useCacheFirst: $useCacheFirst
      versionData: $versionData
    ) {
      ...PrinterContextProvider_PdfPrint_Fragment
    }
  }
`);

export const PrinterContextProvider_PrintSectionTemplate_Mutation = graphql(`
  mutation PrinterContextProvider_PrintSectionTemplate(
    $clearTmp: Boolean!
    $isTmp: Boolean!
    $printableId: Id!
    $useCacheFirst: Boolean!
    $versionData: VersionDataInput
  ) {
    print: printSectionTemplate(
      clearTmp: $clearTmp
      isTmp: $isTmp
      printableId: $printableId
      useCacheFirst: $useCacheFirst
      versionData: $versionData
    ) {
      ...PrinterContextProvider_PdfPrint_Fragment
    }
  }
`);

export const PrinterContextProvider_PrintReport_Mutation = graphql(`
  mutation PrinterContextProvider_PrintReport(
    $clearTmp: Boolean!
    $isTmp: Boolean!
    $printableId: Id!
    $useCacheFirst: Boolean!
    $versionData: VersionDataInput
  ) {
    print: printReport(
      clearTmp: $clearTmp
      isTmp: $isTmp
      printableId: $printableId
      useCacheFirst: $useCacheFirst
      versionData: $versionData
    ) {
      ...PrinterContextProvider_PdfPrint_Fragment
    }
  }
`);

export const PrinterContextProvider_PrintQuotation_Mutation = graphql(`
  mutation PrinterContextProvider_PrintQuotation(
    $clearTmp: Boolean!
    $isTmp: Boolean!
    $printableId: Id!
    $useCacheFirst: Boolean!
    $versionData: VersionDataInput
  ) {
    print: printQuotation(
      clearTmp: $clearTmp
      isTmp: $isTmp
      printableId: $printableId
      useCacheFirst: $useCacheFirst
      versionData: $versionData
    ) {
      ...PrinterContextProvider_PdfPrint_Fragment
    }
  }
`);

type PrinterContextProviderMutations =
  | typeof PrinterContextProvider_PrintDocumentTemplate_Mutation
  | typeof PrinterContextProvider_PrintSectionTemplate_Mutation
  | typeof PrinterContextProvider_PrintReport_Mutation
  | typeof PrinterContextProvider_PrintQuotation_Mutation;

type PrinterContextProviderMutationResults =
  ResultOf<PrinterContextProviderMutations>;

type Command =
  | 'printDocumentTemplate'
  | 'printSectionTemplate'
  | 'printMissionReport'
  | 'printMissionQuotation';

export type PrintHandlerParams = Omit<
  VariablesOf<PrinterContextProviderMutations>,
  'versionData'
> &
  Readonly<{
    command: Command;
    handleError?: () => void;
    handleSuccess?: (
      mutationResultData: Readonly<PrinterContextProvider_PdfPrint>,
    ) => void;
    memos: MemosState;
    queryKeysToInvalidate: readonly QueryKey[];
    snackbarActionTarget: string;
  }>;

export type PrintHandler = (params: Readonly<PrintHandlerParams>) => void;

interface PrinterData {
  readonly print: PrintHandler;
}

const PrinterContext = createContext<PrinterData>({
  print: NULL_FUNCTION,
});

interface Props {
  readonly children: ReactNode;
}

const PrinterContextProvider = ({ children }: Readonly<Props>) => {
  const queryClient = useQueryClient();

  const { startBackgroundOperation, endBackgroundOperation } = useUiContext();

  const { enqueueSnackbar } = useSnackbar();
  const { buildViewSnackbarAction } = useViewSnackbarAction();

  const { pathname: currentPath } = useLocation();

  const { mutate: printDocumentTemplateMutation } = useGraphQLMutation(
    PrinterContextProvider_PrintDocumentTemplate_Mutation,
  );

  const { mutate: printSectionTemplateMutation } = useGraphQLMutation(
    PrinterContextProvider_PrintSectionTemplate_Mutation,
  );

  const { mutate: printMissionReportMutation } = useGraphQLMutation(
    PrinterContextProvider_PrintReport_Mutation,
  );

  const { mutate: printMissionQuotationMutation } = useGraphQLMutation(
    PrinterContextProvider_PrintQuotation_Mutation,
  );

  const [snackbarActionTargets, setSnackbarActionTargets] =
    useState<readonly string[]>(EMPTY_ARRAY);

  const value = useMemo(() => {
    const print = ({
      clearTmp,
      command,
      handleError,
      handleSuccess,
      isTmp,
      memos,
      printableId,
      queryKeysToInvalidate,
      snackbarActionTarget,
      useCacheFirst,
    }: Readonly<PrintHandlerParams>) => {
      const backgroundOperationKey = {
        type: BackgroundOperationType.Print,
        identifier: printableId,
      };

      startBackgroundOperation(backgroundOperationKey);

      const printerMutationMap = {
        printDocumentTemplate: { printMutation: printDocumentTemplateMutation },
        printSectionTemplate: { printMutation: printSectionTemplateMutation },
        printMissionReport: { printMutation: printMissionReportMutation },
        printMissionQuotation: { printMutation: printMissionQuotationMutation },
      } as const;

      const onSuccess = (
        data: Readonly<PrinterContextProviderMutationResults>,
      ) => {
        const result = getFragmentData(
          PrinterContextProvider_PdfPrint_Fragment,
          data.print,
        );

        endBackgroundOperation(backgroundOperationKey);

        setSnackbarActionTargets((prev) => [...prev, snackbarActionTarget]);

        queryKeysToInvalidate.forEach((queryKey) => {
          void queryClient.invalidateQueries({ queryKey });
        });

        handleSuccess?.(result);
      };

      const onError = (error: unknown) => {
        endBackgroundOperation(backgroundOperationKey);

        enqueueSnackbar(
          <MessageList messages={userFriendlyErrors(error as BackError)} />,
          {
            variant: 'error',
          },
        );

        handleError?.();
      };

      const { printMutation } = printerMutationMap[command];

      printMutation(
        {
          clearTmp,
          isTmp,
          printableId,
          useCacheFirst,
          versionData: memoForm2ApiMapper(memos),
        },
        {
          onError,
          onSuccess,
        },
      );
    };

    return { print };
  }, [
    enqueueSnackbar,
    printDocumentTemplateMutation,
    printMissionQuotationMutation,
    printMissionReportMutation,
    printSectionTemplateMutation,
    queryClient,
    startBackgroundOperation,
    endBackgroundOperation,
  ]);

  useEffect(() => {
    if (!snackbarActionTargets.length) {
      return;
    }

    // eslint-disable-next-line functional/no-loop-statements
    for (const snackbarActionTarget of snackbarActionTargets) {
      // eslint-disable-next-line functional/no-conditional-statements
      if (currentPath !== snackbarActionTarget) {
        enqueueSnackbar("L'impression PDF est terminée.", {
          action: buildViewSnackbarAction(snackbarActionTarget),
        });
      }

      // TODO : check if we can refactor this code in order to respect this rule
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSnackbarActionTargets((prev) =>
        prev.filter((p) => p !== snackbarActionTarget),
      );
    }
  }, [
    snackbarActionTargets,
    buildViewSnackbarAction,
    currentPath,
    enqueueSnackbar,
  ]);

  return (
    <PrinterContext.Provider value={value}>{children}</PrinterContext.Provider>
  );
};

export { PrinterContext, PrinterContextProvider };
